import { Workflow, WorkflowNode } from './types';

interface TaskPayload {
  type: string;
  name: string;
  account_id: string;
  priority?: number;
  quick_seed_config?: {
    action: string;
    post_url?: string;
    target?: string;
    content?: string;
    keywords?: string[];
    urls?: string[];
    max_results?: number;
  };
  schedule?: {
    frequency: string;
    start_date?: string;
    end_date?: string;
  };
  notes?: string;
}

export class WorkflowConverter {
  /**
   * Convert workflow nodes to task list for a specific account
   */
  static async convertToTasks(
    workflow: Workflow,
    accountId: string
  ): Promise<TaskPayload[]> {
    const tasks: TaskPayload[] = [];
    const sortedNodes = this.topologicalSort(workflow.nodes, workflow.edges);
    
    let currentTime = new Date();
    
    for (const node of sortedNodes) {
      // Skip control nodes
      if (node.type === 'start' || node.type === 'end') {
        continue;
      }
      
      // Handle delay nodes
      if (node.type === 'delay') {
        const config = node.data.config;
        const delaySeconds = this.randomBetween(
          config.min_seconds || 60,
          config.max_seconds || 300
        );
        currentTime = new Date(currentTime.getTime() + delaySeconds * 1000);
        continue;
      }
      
      // Convert action node to task
      const task = this.nodeToTask(node, accountId, currentTime);
      if (task) {
        tasks.push(task);
        
        // Add small buffer between tasks (30-60s)
        currentTime = new Date(
          currentTime.getTime() + this.randomBetween(30, 60) * 1000
        );
      }
    }
    
    return tasks;
  }
  
  /**
   * Topological sort to get execution order
   */
  private static topologicalSort(
    nodes: WorkflowNode[],
    edges: Array<{ source: string; target: string }>
  ): WorkflowNode[] {
    // Build adjacency list
    const graph = new Map<string, string[]>();
    const inDegree = new Map<string, number>();
    
    // Initialize
    nodes.forEach((node) => {
      graph.set(node.id, []);
      inDegree.set(node.id, 0);
    });
    
    // Build graph
    edges.forEach((edge) => {
      graph.get(edge.source)?.push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    });
    
    // Find start nodes (in-degree = 0)
    const queue: string[] = [];
    inDegree.forEach((degree, nodeId) => {
      if (degree === 0) {
        queue.push(nodeId);
      }
    });
    
    const result: WorkflowNode[] = [];
    const nodeMap = new Map(nodes.map((n) => [n.id, n]));
    
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      const node = nodeMap.get(nodeId);
      if (node) {
        result.push(node);
      }
      
      const neighbors = graph.get(nodeId) || [];
      neighbors.forEach((neighborId) => {
        const newDegree = (inDegree.get(neighborId) || 0) - 1;
        inDegree.set(neighborId, newDegree);
        if (newDegree === 0) {
          queue.push(neighborId);
        }
      });
    }
    
    return result;
  }
  
  /**
   * Convert node to task payload
   */
  private static nodeToTask(
    node: WorkflowNode,
    accountId: string,
    executeAt: Date
  ): TaskPayload | null {
    const config = node.data.config;
    const label = node.data.label || node.type;
    
    let quickSeedConfig: any = {};
    
    switch (node.type) {
      case 'search_groups':
        quickSeedConfig = {
          action: 'search_groups',
          post_url: 'https://facebook.com',
          keywords: config.keywords || ['default'],
          max_results: config.max_results || 20,
        };
        break;
        
      case 'join_groups':
        quickSeedConfig = {
          action: 'join_group',
          post_url: config.group_id || 'https://facebook.com',
          target: config.group_id || '',
        };
        break;
        
      case 'post_to_group':
        quickSeedConfig = {
          action: 'post_to_group',
          post_url: config.group_id || 'https://facebook.com',
          target: config.group_id || '',
          content: config.content || '<AI_GENERATED>',
        };
        break;
        
      case 'post_to_profile':
        quickSeedConfig = {
          action: 'post_to_profile',
          post_url: 'https://facebook.com',
          content: config.content || '<AI_GENERATED>',
        };
        break;

      case 'create_post_instagram':
        quickSeedConfig = {
          action: 'create_post_instagram',
          content: node.data.create_post_instagram_config?.content_hints || config.content || '<AI_GENERATED>',
        };
        break;

      case 'follow_instagram':
        quickSeedConfig = {
          action: 'follow_instagram',
          urls: node.data.follow_instagram_config?.urls || config.urls || [],
        };
        break;

      case 'comment_post_instagram':
        quickSeedConfig = {
          action: 'comment_post_instagram',
          urls: node.data.comment_post_instagram_config?.urls || config.urls || [],
          content: node.data.comment_post_instagram_config?.content_hints || config.content || '<AI_GENERATED>',
        };
        break;

      case 'reply_comment_instagram':
        quickSeedConfig = {
          action: 'reply_comment_instagram',
          urls: node.data.reply_comment_instagram_config?.urls || config.urls || [],
          keywords: node.data.reply_comment_instagram_config?.keywords || config.keywords || [],
          content: node.data.reply_comment_instagram_config?.reply_template || config.content || '<AI_GENERATED>',
        };
        break;
        
      case 'comment_on_post':
        quickSeedConfig = {
          action: 'comment_on_post',
          post_url: config.post_url || 'https://facebook.com',
          target: config.post_url || '',
          content: config.comment_text || '<AI_GENERATED>',
        };
        break;
        
      default:
        return null;
    }
    
    const endDate = new Date(executeAt);
    endDate.setDate(endDate.getDate() + 7); // 7 days validity
    
    return {
      type: 'quick_seed',
      name: `${label} - Workflow Task`,
      account_id: accountId,
      priority: 5,
      quick_seed_config: quickSeedConfig,
      schedule: {
        frequency: 'once',
        start_date: executeAt.toISOString(),
        end_date: endDate.toISOString(),
      },
      notes: `Generated from workflow: ${label}`,
    };
  }
  
  /**
   * Generate random number between min and max
   */
  private static randomBetween(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
  
  /**
   * Validate workflow before execution
   */
  static validateWorkflow(workflow: Workflow): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    // Check if workflow has nodes
    if (!workflow.nodes || workflow.nodes.length === 0) {
      errors.push('Workflow must have at least one node');
    }
    
    // Check for start node
    const hasStart = workflow.nodes.some((n) => n.type === 'start');
    if (!hasStart) {
      errors.push('Workflow must have a start node');
    }
    
    // Check for end node
    const hasEnd = workflow.nodes.some((n) => n.type === 'end');
    if (!hasEnd) {
      errors.push('Workflow must have an end node');
    }
    
    // Check for cycles
    const hasCycle = this.detectCycle(workflow.nodes, workflow.edges);
    if (hasCycle) {
      errors.push('Workflow contains cycles');
    }
    
    // Check for orphaned nodes
    const connectedNodes = new Set<string>();
    workflow.edges.forEach((edge) => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });
    
    const orphans = workflow.nodes.filter(
      (node) => node.type !== 'start' && node.type !== 'end' && !connectedNodes.has(node.id)
    );
    
    if (orphans.length > 0) {
      errors.push(`${orphans.length} node(s) are not connected`);
    }
    
    return {
      valid: errors.length === 0,
      errors,
    };
  }
  
  /**
   * Detect cycles in workflow
   */
  private static detectCycle(
    nodes: WorkflowNode[],
    edges: Array<{ source: string; target: string }>
  ): boolean {
    const graph = new Map<string, string[]>();
    nodes.forEach((node) => graph.set(node.id, []));
    edges.forEach((edge) => {
      graph.get(edge.source)?.push(edge.target);
    });
    
    const visited = new Set<string>();
    const recStack = new Set<string>();
    
    const dfs = (nodeId: string): boolean => {
      visited.add(nodeId);
      recStack.add(nodeId);
      
      const neighbors = graph.get(nodeId) || [];
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          if (dfs(neighbor)) return true;
        } else if (recStack.has(neighbor)) {
          return true; // Cycle detected
        }
      }
      
      recStack.delete(nodeId);
      return false;
    };
    
    for (const node of nodes) {
      if (!visited.has(node.id)) {
        if (dfs(node.id)) return true;
      }
    }
    
    return false;
  }
}
